if (!customElements.get("personalize-product")) {
  customElements.define(
    "personalize-product",
    class PersonalizeProduct extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;
        this.initialized = true;

        this.productInfo = this.closest("product-info");
        this.gallery = this.productInfo?.querySelector("media-gallery");
        this.details = this.querySelector("details");
        this.form = document.getElementById(`product-form-${this.dataset.sectionId}`);
        this.fontProperty = this.querySelector("[data-personalize-font-property]");
        this.inputs = {
          front: this.querySelector('[data-personalize-input="front"]'),
          back: this.querySelector('[data-personalize-input="back"]'),
        };
        this.activeSide = "front";
        this.fontFamily = this.querySelector("[data-personalize-font]:checked")?.value || "Playfair Display";
        this.config = this.getConfig();
        Object.values(this.inputs).forEach((input) => {
          if (input) input.style.fontFamily = `"${this.fontFamily}", sans-serif`;
        });

        if (!this.gallery || !this.config) return;

        this.handleInput = this.handleInput.bind(this);
        this.handleClick = this.handleClick.bind(this);
        this.handleChange = this.handleChange.bind(this);
        this.handleToggle = this.handleToggle.bind(this);
        this.handleInvalid = this.handleInvalid.bind(this);
        this.handleSubmit = this.handleSubmit.bind(this);
        this.handleProductClick = this.handleProductClick.bind(this);
        this.handleResize = this.draw.bind(this);

        this.addEventListener("input", this.handleInput);
        this.addEventListener("click", this.handleClick);
        this.addEventListener("change", this.handleChange);
        this.addEventListener("invalid", this.handleInvalid, true);
        this.details?.addEventListener("toggle", this.handleToggle);
        this.form?.addEventListener("submit", this.handleSubmit, true);
        this.productInfo?.addEventListener("click", this.handleProductClick, true);

        this.createCanvases();
        this.observeMedia();
        this.loadFont(this.fontFamily);
      }

      disconnectedCallback() {
        this.removeEventListener("input", this.handleInput);
        this.removeEventListener("click", this.handleClick);
        this.removeEventListener("change", this.handleChange);
        this.removeEventListener("invalid", this.handleInvalid, true);
        this.details?.removeEventListener("toggle", this.handleToggle);
        this.form?.removeEventListener("submit", this.handleSubmit, true);
        this.productInfo?.removeEventListener("click", this.handleProductClick, true);
        window.removeEventListener("resize", this.handleResize);
        this.resizeObserver?.disconnect();
      }

      getConfig() {
        const configElement = this.querySelector("[data-personalize-config]");
        if (!configElement) return null;

        try {
          return JSON.parse(configElement.textContent);
        } catch (error) {
          console.warn("Personalização: configuração inválida.", error);
          return null;
        }
      }

      get mediaIndexes() {
        return {
          front: Number(this.dataset.frontMediaIndex || 0),
          back: Number(this.dataset.backMediaIndex || this.dataset.frontMediaIndex || 0),
        };
      }

      get mediaItems() {
        return [...this.gallery.querySelectorAll(".m-media-gallery__list .m-product-media--item:not(.swiper-slide-duplicate)")];
      }

      getMediaIndex(side) {
        const requestedIndex = this.mediaIndexes[side];
        if (this.mediaItems[requestedIndex]) return requestedIndex;
        if (side === "back" && this.mediaItems[this.mediaIndexes.front]) return this.mediaIndexes.front;
        return 0;
      }

      createCanvases() {
        Object.keys(this.mediaIndexes).forEach((side) => {
          if (!this.inputs[side]) return;

          this.getMediaElements(side).forEach((media) => {
            const mediaContent = media.querySelector(".m-product-media");
            if (!mediaContent || mediaContent.querySelector(`[data-personalize-canvas="${side}"]`)) return;

            mediaContent.style.position = "relative";
            const canvas = document.createElement("canvas");
            canvas.className = "tp-personalize-preview";
            canvas.dataset.personalizeCanvas = side;
            canvas.setAttribute("aria-hidden", "true");
            mediaContent.appendChild(canvas);
          });
        });

        this.draw();
      }

      getMediaElements(side) {
        const index = this.getMediaIndex(side);
        const elements = [...this.gallery.querySelectorAll(`.m-media-gallery__list .m-product-media--item[data-index="${index}"]`)];
        return elements.length ? elements : [this.mediaItems[index]].filter(Boolean);
      }

      getCanvases(side) {
        return this.getMediaElements(side)
          .map((media) => media.querySelector(`[data-personalize-canvas="${side}"]`))
          .filter(Boolean);
      }

      observeMedia() {
        if (!window.ResizeObserver) {
          window.addEventListener("resize", this.handleResize, { passive: true });
          return;
        }

        this.resizeObserver = new ResizeObserver(() => this.draw());
        Object.keys(this.inputs).forEach((side) => {
          this.getCanvases(side).forEach((canvas) => {
            if (canvas.parentElement) this.resizeObserver.observe(canvas.parentElement);
          });
        });
      }

      handleToggle() {
        if (!this.details.open) return;
        this.createCanvases();
        this.showMedia(this.activeSide);
        this.draw();
      }

      handleInput(event) {
        const input = event.target.closest("[data-personalize-input]");
        if (!input) return;

        if (this.dataset.uppercase === "true") {
          const selectionStart = input.selectionStart;
          const selectionEnd = input.selectionEnd;
          input.value = input.value.toLocaleUpperCase("pt-BR");
          input.setSelectionRange(selectionStart, selectionEnd);
        }

        const side = input.dataset.personalizeInput;
        const count = this.querySelector(`[data-personalize-count="${side}"]`);
        if (count) count.textContent = input.value.length;

        this.setActiveSide(side);
        this.draw();
      }

      handleClick(event) {
        const sideButton = event.target.closest("[data-personalize-side]");
        if (!sideButton) return;
        this.setActiveSide(sideButton.dataset.personalizeSide);
      }

      handleChange(event) {
        const fontInput = event.target.closest("[data-personalize-font]");
        if (!fontInput) return;

        this.fontFamily = fontInput.value;
        if (this.fontProperty) this.fontProperty.value = this.fontFamily;
        Object.values(this.inputs).forEach((input) => {
          if (input) input.style.fontFamily = `"${this.fontFamily}", sans-serif`;
        });
        this.loadFont(this.fontFamily);
      }

      handleInvalid(event) {
        const input = event.target.closest("[data-personalize-input]");
        if (!input) return;
        if (this.details) this.details.open = true;
        this.setActiveSide(input.dataset.personalizeInput);
      }

      handleSubmit() {
        this.revealFirstMissingField();
      }

      handleProductClick(event) {
        if (!event.target.closest(".m-product-dynamic-checkout")) return;
        this.revealFirstMissingField();
      }

      revealFirstMissingField() {
        const missingInput = Object.values(this.inputs).find((input) => input?.required && !input.value.trim());
        if (!missingInput) return;
        if (this.details) this.details.open = true;
        this.setActiveSide(missingInput.dataset.personalizeInput);
      }

      setActiveSide(side) {
        if (!this.inputs[side]) return;
        this.activeSide = side;

        this.querySelectorAll("[data-personalize-side]").forEach((button) => {
          const isActive = button.dataset.personalizeSide === side;
          button.classList.toggle("is-active", isActive);
          button.setAttribute("aria-pressed", String(isActive));
        });

        this.querySelectorAll("[data-personalize-field]").forEach((field) => {
          field.hidden = field.dataset.personalizeField !== side;
        });

        this.showMedia(side);
        this.draw();
      }

      showMedia(side) {
        const index = this.getMediaIndex(side);
        const media = this.mediaItems[index];
        if (!media) return;

        if (this.gallery.slider && typeof this.gallery.slider.slideToLoop === "function") {
          this.gallery.slider.slideToLoop(index);
        } else if (this.gallery.dataset.layout !== "layout-4" && this.gallery.dataset.layout !== "layout-6") {
          media.scrollIntoView({ behavior: "smooth", block: "nearest" });
        }
      }

      async loadFont(fontFamily) {
        if (document.fonts?.load) {
          await document.fonts.load(`16px "${fontFamily}"`);
        }
        this.draw();
      }

      draw() {
        const baseFontSize = Number(this.dataset.fontSize) || 53;
        const textColor = this.dataset.textColor || "#6b5629";
        const devicePixelRatio = window.devicePixelRatio || 1;

        this.config.fields.forEach((field) => {
          const side = field.id;
          const input = this.inputs[side];
          if (!input) return;

          this.getCanvases(side).forEach((canvas) => {
            const container = canvas.parentElement;
            const width = container.clientWidth;
            const height = container.clientHeight;
            if (!width || !height) return;

            canvas.width = Math.round(width * devicePixelRatio);
            canvas.height = Math.round(height * devicePixelRatio);
            canvas.style.width = `${width}px`;
            canvas.style.height = `${height}px`;

            const context = canvas.getContext("2d");
            context.scale(devicePixelRatio, devicePixelRatio);
            context.clearRect(0, 0, width, height);
            context.fillStyle = textColor;
            context.font = `${(baseFontSize * width) / 478}px "${this.fontFamily}", sans-serif`;
            context.textAlign = "center";
            context.textBaseline = "middle";
            context.fillText(
              input.value,
              (Math.min(100, Math.max(0, Number(field.axisX))) / 100) * width,
              (Math.min(100, Math.max(0, Number(field.axisY))) / 100) * height
            );
          });
        });
      }
    }
  );
}
